import { useEffect, useMemo, useState } from 'react';
import {
  Activity, BarChart3, Boxes, CalendarClock, Cat, ChevronDown, CircleDollarSign, FlaskConical,
  Gamepad2, Gift, LayoutDashboard, LogOut, Megaphone, Menu, PanelLeftClose,
  Rocket, Search, Settings, ShieldCheck, Sparkles, Users, WandSparkles, X,
} from 'lucide-react';
import { AdminApi, type AdminActor, type Environment, type Platform } from './api';
import { Dashboard } from './pages/Dashboard';
import { EconomyActivityCenter } from './pages/EconomyActivityCenter';
import { ConfigCenter } from './pages/ConfigCenter';
import { DailyBenefitCenter } from './pages/DailyBenefitCenter';
import { DailySignInCenter } from './pages/DailySignInCenter';
import { DailyTasksCenter } from './pages/DailyTasksCenter';
import { AchievementsCenter } from './pages/AchievementsCenter';
import { SocialRewardsCenter } from './pages/SocialRewardsCenter';
import { LevelEditor } from './pages/LevelEditor';
import { ReleaseCenter } from './pages/ReleaseCenter';
import { SystemCenter } from './pages/SystemCenter';
import { WeeklyCompetitionCenter } from './pages/WeeklyCompetitionCenter';
import { BoxLeaderboardCenter } from './pages/BoxLeaderboardCenter';

const runtimeApiBase = window.location.pathname === '/trial-admin'
  || window.location.pathname.startsWith('/trial-admin/')
  ? '/trial-api'
  : '';
const api = new AdminApi(import.meta.env.VITE_ADMIN_API_BASE ?? runtimeApiBase);
const GAME_ID = 'miao-zai-er-gui';

type View = 'overview' | 'levels' | 'players' | 'economyActivity' | 'ads' | 'economy' | 'features' | 'activities' | 'social' | 'socialRewards' | 'dailyBenefit' | 'dailySignIn' | 'dailyTasks' | 'achievements' | 'weekly' | 'boxes' | 'boxLeaderboard' | 'editor' | 'release' | 'system';

const navigation: Array<{ section: string; items: Array<{ id: View; label: string; icon: typeof Activity; badge?: string }> }> = [
  { section: '观察', items: [
    { id: 'overview', label: '经营总览', icon: LayoutDashboard },
    { id: 'levels', label: '关卡分析', icon: BarChart3 },
    { id: 'players', label: '玩家查询', icon: Users },
    { id: 'economyActivity', label: '鱼干与道具流水', icon: CircleDollarSign },
  ] },
  { section: '运营', items: [
    { id: 'ads', label: '广告中心', icon: Megaphone },
    { id: 'economy', label: '鱼干经济', icon: CircleDollarSign },
    { id: 'features', label: '首页功能', icon: LayoutDashboard },
    { id: 'activities', label: '活动任务', icon: Gift },
    { id: 'social', label: '社交裂变', icon: Users, badge: '新' },
    { id: 'socialRewards', label: '社交奖励', icon: Gift, badge: '新' },
    { id: 'weekly', label: '周赛配置', icon: CalendarClock, badge: '筹备' },
    { id: 'dailyBenefit', label: '每日奖励', icon: Sparkles, badge: '新' },
    { id: 'dailySignIn', label: '每日签到', icon: CalendarClock, badge: '新' },
    { id: 'dailyTasks', label: '每日任务', icon: Gift, badge: '新' },
    { id: 'achievements', label: '成就配置', icon: Sparkles, badge: '新' },
    { id: 'boxes', label: '神秘猫箱', icon: Boxes, badge: '审' },
    { id: 'boxLeaderboard', label: '箱底真价名家榜', icon: BarChart3, badge: '新' },
  ] },
  { section: '生产', items: [
    { id: 'editor', label: '关卡工坊', icon: WandSparkles },
    { id: 'release', label: '发布中心', icon: Rocket },
    { id: 'system', label: '系统管理', icon: Settings },
  ] },
];

export function App() {
  const [actor, setActor] = useState<AdminActor | null>(null);
  const [checking, setChecking] = useState(true);
  const [view, setView] = useState<View>('overview');
  const [environment, setEnvironment] = useState<Environment>('development');
  const [deploymentTargetId, setDeploymentTargetId] = useState('local');
  const [platform, setPlatform] = useState<Platform>('all');
  const [sidebarOpen, setSidebarOpen] = useState(() => window.innerWidth >= 1100);
  const [commandOpen, setCommandOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    api.me().then(({ actor }) => setActor(actor)).catch(() => undefined).finally(() => setChecking(false));
    api.health().then((health) => { setEnvironment(health.environment); setDeploymentTargetId(health.deploymentTargetId); }).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 3200);
    return () => window.clearTimeout(timer);
  }, [toast]);

  const title = useMemo(() => navigation.flatMap((group) => group.items).find((item) => item.id === view)?.label ?? '经营总览', [view]);
  if (checking) return <Splash />;
  if (!actor) return <Login onSuccess={setActor} />;

  const shared = { api, gameId: GAME_ID, environment, platform, notify: setToast };
  return (
    <div className={`app-shell ${sidebarOpen ? '' : 'sidebar-collapsed'}`}>
      <aside className="sidebar">
        <div className="brand-lockup">
          <div className="brand-mark"><Cat size={24} strokeWidth={2.4} /></div>
          <div className="brand-copy"><strong>猫载超市</strong><span>运营驾驶舱</span></div>
          <button className="icon-button collapse-button" onClick={() => setSidebarOpen(false)} aria-label="收起侧栏"><PanelLeftClose size={18} /></button>
        </div>
        <div className="game-switcher">
          <div className="game-avatar">喵</div>
          <div><strong>喵载而归</strong><span>miao-zai-er-gui</span></div>
          <ChevronDown size={16} />
        </div>
        <nav>
          {navigation.map((group) => <div className="nav-group" key={group.section}>
            <p>{group.section}</p>
            {group.items.map((item) => <button key={item.id} className={view === item.id ? 'active' : ''} onClick={() => setView(item.id)}>
              <item.icon size={18} /><span>{item.label}</span>{item.badge && <em>{item.badge}</em>}
            </button>)}
          </div>)}
        </nav>
        <div className="sidebar-footer">
          <div className="service-health"><i /><div><strong>{environmentName(environment)}服务正常</strong><span>{deploymentTargetId} · HTTP</span></div></div>
          <button onClick={async () => { await api.logout(); setActor(null); }}><LogOut size={17} />退出登录</button>
        </div>
      </aside>

      <main className="workspace">
        <header className="topbar">
          <div className="topbar-title">
            {!sidebarOpen && <button className="icon-button" onClick={() => setSidebarOpen(true)}><Menu size={20} /></button>}
            <div><span>猫载超市 / {title}</span><h1>{title}</h1></div>
          </div>
          <div className="topbar-actions">
            <button className="command-trigger" onClick={() => setCommandOpen(true)}><Search size={16} />搜索玩家、关卡、版本 <kbd>⌘ K</kbd></button>
            <label className="select-control platform-select"><Gamepad2 size={16} /><select value={platform} onChange={(event) => setPlatform(event.target.value as Platform)}>
              <option value="all">全部渠道</option><option value="wechat">微信</option><option value="douyin">抖音</option><option value="taptap">TapTap</option>
            </select></label>
            <label className={`select-control environment-select ${environment}`} title="每个环境使用独立服务与 PostgreSQL；切换环境请打开对应部署目标"><FlaskConical size={16} /><select value={environment} onChange={(event) => setEnvironment(event.target.value as Environment)}>
              <option value={environment}>{environmentName(environment)}环境</option>
            </select></label>
            <button className="profile-chip"><span>{actor.displayName.slice(0, 1)}</span><div><strong>{actor.displayName}</strong><em>{roleName(actor.role)}</em></div></button>
          </div>
        </header>

        <div className="page-scroll">
          {view === 'overview' && <Dashboard {...shared} mode="overview" />}
          {view === 'levels' && <Dashboard {...shared} mode="levels" />}
          {view === 'players' && <Dashboard {...shared} mode="players" />}
          {view === 'economyActivity' && <EconomyActivityCenter {...shared} />}
          {view === 'ads' && <ConfigCenter {...shared} section="ads" />}
          {view === 'economy' && <ConfigCenter {...shared} section="economy" />}
          {view === 'features' && <ConfigCenter {...shared} section="features" />}
          {view === 'activities' && <ConfigCenter {...shared} section="activities" />}
          {view === 'social' && <ConfigCenter {...shared} section="social" />}
          {view === 'socialRewards' && <SocialRewardsCenter {...shared} />}
          {view === 'weekly' && <WeeklyCompetitionCenter {...shared} />}
          {view === 'dailyBenefit' && <DailyBenefitCenter {...shared} />}
          {view === 'dailySignIn' && <DailySignInCenter {...shared} />}
          {view === 'dailyTasks' && <DailyTasksCenter {...shared} />}
          {view === 'achievements' && <AchievementsCenter {...shared} />}
          {view === 'boxes' && <ConfigCenter {...shared} section="boxes" />}
          {view === 'boxLeaderboard' && <BoxLeaderboardCenter api={api} environment={environment} notify={setToast} />}
          {view === 'editor' && <LevelEditor {...shared} />}
          {view === 'release' && <ReleaseCenter {...shared} />}
          {view === 'system' && <SystemCenter {...shared} actor={actor} />}
        </div>
      </main>
      {toast && <div className="toast"><ShieldCheck size={18} />{toast}</div>}
      {commandOpen && <CommandPalette close={() => setCommandOpen(false)} navigate={(next) => { setView(next); setCommandOpen(false); }} />}
    </div>
  );
}

function Login({ onSuccess }: { onSuccess: (actor: AdminActor) => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  return <div className="login-page">
    <div className="login-atmosphere"><span className="orb orb-one" /><span className="orb orb-two" /></div>
    <section className="login-story">
      <div className="login-logo"><Cat size={29} /><span>猫载超市运营台</span></div>
      <div className="story-copy"><p>ONE STORE, EVERY SIGNAL</p><h1>看清每一条<br />回家的路线。</h1><span>把渠道、关卡、鱼干与发布状态放进同一个可靠的驾驶舱。</span></div>
      <div className="mini-dashboard"><div className="mini-kpi"><span>今日活跃</span><strong>—</strong><i /></div><div className="mini-bars">{[36, 62, 48, 78, 69, 91, 82].map((height, index) => <b key={index} style={{ height: `${height}%` }} />)}</div><div className="mini-status"><Sparkles size={15} />正式数据与体验数据物理隔离</div></div>
    </section>
    <form className="login-card" onSubmit={async (event) => {
      event.preventDefault(); setBusy(true); setError('');
      try { onSuccess((await api.login(username, password)).actor); }
      catch (reason) { setError(reason instanceof Error ? reason.message : '登录失败'); }
      finally { setBusy(false); }
    }}>
      <div className="login-card-head"><span>SECURE OPERATIONS</span><h2>欢迎回来</h2><p>使用运营账号和密码登录。</p></div>
      <label>账号<input autoFocus autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="name@ops" /></label>
      <label>密码<input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="至少 12 位" /></label>
      {error && <div className="form-error">{error}</div>}
      <button className="primary-button" disabled={busy || username.length < 3 || password.length < 12}>{busy ? '正在验证…' : '进入运营台'}</button>
      <p className="security-note"><ShieldCheck size={15} />安全 Cookie · CSRF 防护 · 操作审计</p>
    </form>
  </div>;
}

function Splash() { return <div className="splash"><div className="brand-mark"><Cat size={30} /></div><strong>正在核对运营环境</strong><span>不会混合体验与正式数据</span></div>; }

function CommandPalette({ close, navigate }: { close: () => void; navigate: (view: View) => void }) {
  return <div className="modal-backdrop" onMouseDown={close}><div className="command-palette" onMouseDown={(event) => event.stopPropagation()}>
    <div className="command-input"><Search size={19} /><input autoFocus placeholder="搜索玩家 ID、关卡或功能…" /><button onClick={close}><X size={18} /></button></div>
    <p>快速前往</p><div className="command-grid">{navigation.flatMap((group) => group.items).map((item) => <button key={item.id} onClick={() => navigate(item.id)}><item.icon size={18} /><span>{item.label}</span></button>)}</div>
  </div></div>;
}

function roleName(role: AdminActor['role']) { return ({ viewer: '观察员', editor: '配置编辑', publisher: '发布负责人', admin: '系统管理员' })[role]; }
function environmentName(environment: Environment) { return environment === 'production' ? '正式' : environment === 'test' ? '测试' : '体验'; }
